import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import tinycolor from 'tinycolor2';

import { isUsedOnMobile, joinClassNames, SCREEN_TYPE, toHHMMSS } from '../../../util/util';
import { NumberHelper } from '../../../util/number-helper';
import { LiveHelper } from '../../../util/live-helper';
import { setPlayerCurrentPosition } from '../../../actions/playerActions';

import BottomControlSetting from './BottomControlSetting';
import Chapters from './Chapters';

import './BottomControls.scss';

interface BottomControlsProps {
	settings?: any;
	playerHover?: boolean;
	isMaintainProportions?: boolean;
	isInlinePlayer?: boolean;
	queryParams?: any;

	onTouchStart?: (_event: React.TouchEvent) => void;
	onTouchMove?: (_event: React.TouchEvent) => void;
	onMouseEnter?: (_event: React.MouseEvent) => void;
	onMouseLeave?: (_event: React.MouseEvent) => void;
	alignVideoOnAndroidDevices?: (_timeOut: number, _isReset: boolean) => void;
}

const BottomControls: React.FC<BottomControlsProps> = ({
	settings,
	playerHover,
	isMaintainProportions,
	isInlinePlayer,
	queryParams,
	onTouchStart,
	onTouchMove,
	onMouseEnter,
	onMouseLeave,
	alignVideoOnAndroidDevices,
}: BottomControlsProps) => {
	const { t: translator } = useTranslation();
	const dispatch = useDispatch();

	const {
		currentMediaId: mediaId,
		chapters: chaptersConfig,
		videoHoverUrl,
		isMediaLive,
		screenType,
		playlistId,
		mediaType,
		mediasData,
		currentMediaId,
		scenario,
		sceneConfig,
	} = useSelector((state) => (state as any).configurationReducer);
	const { currentPosition, duration: durationReducer } = useSelector((state) => (state as any).playerReducer);

	const previewVideo = useRef<any | undefined>();
	const bottomControlBar = useRef<HTMLElement | null>();
	const mouseDownOnTimeline = useRef<boolean>(false);
	const mouseMoveDirection = useRef<number>(1);

	const [showHoverPlayer, setShowHoverPlayer] = useState(false);
	const [hoverPercentage, setHoverPercentage] = useState(-100);
	const [hoverPixels, setHoverPixels] = useState(0);
	const [edgePosition, setEdgePosition] = useState('');
	const [indicatorPosition, setIndicatorPosition] = useState(0);
	const [hoverTiming, setHoverTiming] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const [hoveredChapter, setHoveredChapter] = useState<any | null>(null);
	const [progressBarFocused, setProgressBarFocused] = useState(false);

	const currentMedia = useMemo(
		() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId),
		[mediasData, currentMediaId]
	);
	const duration = currentMedia?.duration ?? durationReducer;
	const chapters = useMemo(() => {
		let newDisplayedChapter = [...chaptersConfig].sort((c1, c2) => (c1.start < c2.start ? -1 : 1));
		newDisplayedChapter = newDisplayedChapter.map((chapter, index) => {
			const newData = { ...chapter };
			const nextChapter = index < chaptersConfig.length - 1 ? newDisplayedChapter[index + 1] : null;

			if (nextChapter) {
				newData.end = nextChapter.start - 0.001;
			}
			if (index === chaptersConfig.length - 1) {
				newData.end = duration;
			}
			if (index === 0) {
				newData.start = 0;
			}

			const width = ((newData.end - newData.start) / duration) * 100;
			return {
				...newData,
				width: width,
			};
		});

		return newDisplayedChapter;
	}, [chaptersConfig, duration]);

	const currentPlayerId = useMemo(() => `player-${playlistId || mediaId}`, [mediaId, playlistId]);
	const hasChapters = useMemo(() => chapters && chapters.length > 0, [chapters]);
	const tabIndexValue = useMemo(() => (isUsedOnMobile(queryParams) ? -1 : 0), [queryParams]);
	const { color, primaryColor } = useMemo(() => {
		const playerColor = settings?.playerStyling?.primaryColor ?? '#126cfc';
		return {
			color: playerColor,
			primaryColor: { backgroundColor: playerColor },
		};
	}, [settings?.playerStyling?.primaryColor]);

	const solidColor = settings?.bottomControls?.solidBackgroundBottomColor ?? '#000000';
	const solidColorStyle = settings.bottomControls.showSolidBackgroundBottomControl
		? {
				backgroundColor: tinycolor({
					...tinycolor(solidColor).toRgb(),
					a: Number(settings.bottomControls.solidBackgroundBottomControlOpacity ?? 100) / 100,
				}).toRgbString(),
			}
		: undefined;

	const goToLive = useCallback(() => {
		if (LiveHelper.isAtLive(isMediaLive, currentPosition, duration)) {
			return;
		}
		(window as any).GoBrain.widgets(currentPlayerId)?.position?.(LiveHelper.LIVE_POSITION_OFFSET);
	}, [currentPlayerId, currentPosition, duration, isMediaLive]);

	const setProgressBarPosition = useCallback(
		(eventTarget: any, xPosition: number, useHoverTiming?: boolean) => {
			const progressBar = eventTarget.classList.contains('bottomControlbarProgress') ? eventTarget : bottomControlBar?.current;
			const bcr = progressBar.getBoundingClientRect();
			const xTiming = Math.round(((xPosition - bcr.left) / bcr.width) * duration * 1000) / 1000;
			const hTiming = isMediaLive ? duration - hoverTiming : hoverTiming;
			const timing = useHoverTiming ? hTiming : xTiming;
			const percentage = (Math.abs(timing) / Math.abs(duration)) * 100;

			if (isMediaLive && Math.floor(Math.abs(hoverTiming)) <= LiveHelper.LIVE_EDGE_MIN_OFFSET) {
				goToLive();
			} else {
				dispatch(setPlayerCurrentPosition(timing));
				(window as any).GoBrain.widgets(currentPlayerId)?.position?.(timing);
				setIndicatorPosition(NumberHelper.percentageToPixels(percentage, bottomControlBar?.current));
			}
		},
		[currentPlayerId, dispatch, duration, goToLive, hoverTiming, isMediaLive]
	);

	const setProgressBarPositionWhenMouseMoveOutSideIFrame = useCallback(() => {
		setIsDragging(false);
		mouseDownOnTimeline.current = false;
		const timing = mouseMoveDirection.current === 1 ? duration : 0;
		const percentage = (Math.abs(timing) / Math.abs(duration)) * 100;

		if (LiveHelper.isAtLive(isMediaLive, timing, duration)) {
			goToLive();
		} else {
			dispatch(setPlayerCurrentPosition(timing));
			(window as any).GoBrain.widgets(currentPlayerId)?.position?.(timing);
			setIndicatorPosition(NumberHelper.percentageToPixels(percentage, bottomControlBar?.current));
		}
	}, [currentPlayerId, dispatch, goToLive, isMediaLive, duration]);

	const moveProgressBarPosition = useCallback(
		(eventTarget: any, xPosition: number) => {
			const progressBar = eventTarget.classList.contains('bottomControlbarProgress') ? eventTarget : bottomControlBar?.current;
			const bcr = progressBar.getBoundingClientRect();
			const result = ((xPosition + 1 - bcr.left) / bcr.width) * 100;
			const timing = ((xPosition + 1 - bcr.left) / bcr.width * duration);
			mouseMoveDirection.current = timing < currentPosition ? -1 : 1;

			if (mouseDownOnTimeline.current) {
				if (result >= 99) {
					goToLive();
				} else {
					setIndicatorPosition(NumberHelper.percentageToPixels(result, bottomControlBar?.current));
				}
			}

			let hoverPixelsToSet = xPosition +1;
			// Handle the preview video outside of player, preview video max-with is 200 -> hover is the center point -> divide by 2 -> 100
			let paddingValue = hasChapters ? (isUsedOnMobile(queryParams) ? 50 : 105) : isUsedOnMobile(queryParams) ? 20 : 100;
			paddingValue = isMediaLive ? (isUsedOnMobile(queryParams) ? 20 : 30) : paddingValue; // If media is live, there's no preview video

			if (hoverPixelsToSet < paddingValue) {
				hoverPixelsToSet = paddingValue;
			} else if (hoverPixelsToSet + paddingValue > bcr.width) {
				hoverPixelsToSet = bcr.width - paddingValue;
			}

			if (hasChapters) {
				// Make sure long chapter's name can display fully
				const chapterNameWidth = bottomControlBar?.current?.querySelector('.bottomControlbarPreviewVideo-name')?.clientWidth ?? 0;
				if (chapterNameWidth > 200 && !isUsedOnMobile(queryParams)) {
					if (hoverPixelsToSet < chapterNameWidth) {
						hoverPixelsToSet = paddingValue;
						setEdgePosition('left');
					} else if (hoverPixelsToSet + chapterNameWidth - 200 > bcr.width) {
						hoverPixelsToSet = bcr.width - paddingValue;
						setEdgePosition('right');
					} else {
						setEdgePosition('');
					}
				} else {
					setEdgePosition('');
				}
			}

			const newHoverTiming = isMediaLive ? duration - timing : timing;
			setHoverPercentage(result - 100);
			setHoverPixels(hoverPixelsToSet);
			setHoverTiming(newHoverTiming);

			if (chapters && chapters.length > 0) {
				const newHoveredChapter = chapters.find((chapter: any) => chapter.start <= newHoverTiming && newHoverTiming <= chapter.end);
				setHoveredChapter(newHoveredChapter);
			} else {
				setHoveredChapter(null);
			}

			if (previewVideo.current) {
				previewVideo.current.currentTime = timing;
			}
		},
		[duration, currentPosition, hasChapters, queryParams, isMediaLive, chapters, goToLive]
	);

	const handleMouseDownProgressBar = useCallback(
		(event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
			setIsDragging(true);
			setShowHoverPlayer(true);
			mouseDownOnTimeline.current = true;
			moveProgressBarPosition(event.target, (event as any).clientX);
		},
		[moveProgressBarPosition]
	);

	const handleClickOnProgressBar = useCallback(
		(event: React.MouseEvent<HTMLElement> | MouseEvent) => {
			event.stopPropagation();
			setShowHoverPlayer(true);
			setIsDragging(false);
			let timeout1: any = 0;
			if (isUsedOnMobile(queryParams)) {
				timeout1 = setTimeout(() => (mouseDownOnTimeline.current = false), 500);
			} else {
				mouseDownOnTimeline.current = false;
			}
			const timeout2 = setTimeout(() => {
				if (!mouseDownOnTimeline.current) {
					setShowHoverPlayer(false);
				}
			}, 1000);
			setProgressBarPosition(event.target, event.clientX, true);

			return () => {
				timeout1 && clearTimeout(timeout1);
				timeout2 && clearTimeout(timeout2);
			}
		},
		[queryParams, setProgressBarPosition]
	);

	const handleKeyDownOnProgressBar = useCallback(
		(e: React.KeyboardEvent) => {
			if (isUsedOnMobile(queryParams)) {
				return;
			}
			if (e.key === 'ArrowLeft' || e.keyCode === 32) {
				e.preventDefault();
				e.stopPropagation();
				let rewindPosition = currentPosition - 15;
				if (rewindPosition < 0) {
					rewindPosition = 0;
				}
				(window as any).GoBrain.widgets(currentPlayerId)?.position?.(rewindPosition);
			}
			if (e.key === 'ArrowRight' || e.keyCode === 39) {
				e.preventDefault();
				e.stopPropagation();

				if (LiveHelper.isAtLive(isMediaLive, currentPosition, duration)) {
					return;
				}
				const forwardPosition = currentPosition + 15;
				(window as any).GoBrain.widgets(currentPlayerId)?.position?.(forwardPosition);
			}
		},
		[currentPlayerId, currentPosition, duration, isMediaLive, queryParams]
	);

	const handleMouseMoveProgressBar = useCallback(
		(event: React.MouseEvent<HTMLElement> | MouseEvent) => {
			if (isUsedOnMobile(queryParams) || (event?.target as any)?.className.includes('bottomControlbarPreviewVideo') || (event?.target as any)?.className.includes('bottomControlbarHoverPosition')) {
				return;
			}
			event.stopPropagation();
			moveProgressBarPosition(event.target, event.clientX);
		},
		[moveProgressBarPosition, queryParams]
	);

	const handleOnDrag = useCallback(
		(event: DraggableEvent, data: DraggableData) => {
			moveProgressBarPosition(event.target, data.x);
		},
		[moveProgressBarPosition]
	);

	const handleStartDrag = useCallback((e: DraggableEvent, _data: DraggableData) => {
		e.preventDefault();
		setIsDragging(true);
		setShowHoverPlayer(true);
		mouseDownOnTimeline.current = true;
	}, []);

	const handleStopDrag = useCallback(
		(event: DraggableEvent, data: DraggableData) => {
			setIsDragging(false);
			setShowHoverPlayer(false);
			setTimeout(() => (mouseDownOnTimeline.current = false), 500);
			setProgressBarPosition(event.target, data.x);
			setHoverPercentage(-100);
		},
		[setProgressBarPosition]
	);

	const getPercentageBaseOnCurrentPosition = useCallback(
		(unitPer = 100) => {
			if (typeof currentPosition === "string" || currentPosition < 0 || LiveHelper.isAtLive(isMediaLive, currentPosition, duration)) {
				return 100;
			}
			return (Math.abs(currentPosition) / Math.abs(duration)) * unitPer;
		},
		[currentPosition, duration, isMediaLive]
	);

	const handleWindowMouseUp = useCallback(
		(event: MouseEvent) => {
			if (!isDragging) {
				return;
			}
			if ((event.target as any).tagName.toUpperCase() !== 'HTML' || queryParams?.useLoader !== 'true') {
				handleClickOnProgressBar(event);
				return;
			}
			setProgressBarPositionWhenMouseMoveOutSideIFrame();
		},
		[handleClickOnProgressBar, isDragging, queryParams?.useLoader, setProgressBarPositionWhenMouseMoveOutSideIFrame]
	);

	const handleWindowMouseMove = useCallback(
		(event: MouseEvent) => {
			if ((event.target as any).classList.contains('bottomControlbarProgressWrapper') || !isDragging) {
				return;
			}
			handleMouseMoveProgressBar(event);
		},
		[handleMouseMoveProgressBar, isDragging]
	);

	useEffect(() => {
		window.addEventListener('mouseup', handleWindowMouseUp);
		window.addEventListener('mousemove', handleWindowMouseMove);
		return () => {
			window.removeEventListener('mouseup', handleWindowMouseUp);
			window.removeEventListener('mousemove', handleWindowMouseMove);
		};
	}, [handleWindowMouseUp, handleWindowMouseMove]);

	useEffect(() => {
		if (isDragging) {
			return;
		}
		setIndicatorPosition(NumberHelper.percentageToPixels(getPercentageBaseOnCurrentPosition(), bottomControlBar?.current));
	}, [getPercentageBaseOnCurrentPosition, isDragging]);

	useLayoutEffect(() => {
		const chatOverlay = document.getElementsByClassName('chat-overlay-mobile')[0] as any;
		const settings = document.getElementsByClassName('gobrain-window-additional-settings')[0];
		if (chatOverlay && settings) {
			chatOverlay.style.visibility = settings.classList.contains('gobrain-visible') ? 'hidden' : '';
		}
	}, []);

	useEffect(() => {
		(window as any).GoBrain.widgets(currentPlayerId)?.position?.(LiveHelper.LIVE_POSITION_OFFSET);
	}, [])

	const progressTranslate = useMemo(
		() => ({
			transform: `translateX(${NumberHelper.getCurrentProgressPercentage(indicatorPosition, bottomControlBar.current?.clientWidth || 0)}%)`,
		}),
		[indicatorPosition]
	);

	const isShowTimeline = useMemo(() => {
		if (mediaType === 'scenario' && scenario && sceneConfig) {
			const currentSceneData = (scenario.scenes ?? []).find((s: any) => s.id === sceneConfig.currentScene);
			return currentSceneData?.metadata?.timeline?.visibility === undefined || currentSceneData?.metadata?.timeline?.visibility === 'inherit'
				? settings.bottomControls.showTimeline
				: currentSceneData?.metadata?.timeline?.visibility === 'visible';
		}

		const streamType = LiveHelper.getStreamType(isMediaLive, duration);
		return (
			settings.bottomControls.showTimeline &&
			(streamType === LiveHelper.LIVE_STREAM_TYPE_DVR || streamType === LiveHelper.LIVE_STREAM_TYPE_ON_DEMAND)
		);
	}, [settings, mediaType, isMediaLive, duration, scenario, sceneConfig]);

	if (settings?.bottomControls?.enabled === false) {
		return null;
	}

	if (screenType === SCREEN_TYPE.mobile && !isUsedOnMobile(queryParams)) {
		return (
			<div
				className={'bottomControlsContainer'}
				onTouchStart={onTouchStart}
				onTouchMove={onTouchMove}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
			>
				<div className="bottomIdControlsContainer">
					<div
						className={joinClassNames(
							'bottomControlbarWrapper',
							isDragging || playerHover || settings?.bottomControls?.alwaysVisible ? 'bottomControlbarHover' : ''
						)}
					>
						<div className="bottomControlbar" ref={(r) => (bottomControlBar.current = r)}>
							{settings.bottomControls.showSolidBackgroundBottomControl && <div style={solidColorStyle} className="solidBackground" />}
							{isShowTimeline &&
								(LiveHelper.getStreamType(isMediaLive, duration) === LiveHelper.LIVE_STREAM_TYPE_DVR ||
									LiveHelper.getStreamType(isMediaLive, duration) === LiveHelper.LIVE_STREAM_TYPE_ON_DEMAND) && (
									<>
										<div
											data-control-type="slider-control" // Every slider should have this attribute to be able to work.
											className={joinClassNames(
												'bottomControlbarProgressWrapper',
												hasChapters ? 'bottomControlbarProgressWrapper--chapters' : ''
											)}
											onClick={handleClickOnProgressBar}
											onMouseDown={handleMouseDownProgressBar}
											onMouseUp={(e) => e.stopPropagation}
											onMouseMove={handleMouseMoveProgressBar}
											onTouchStart={() => (mouseDownOnTimeline.current = true)}
											onMouseOver={() => {
												setShowHoverPlayer(true);
											}}
											onMouseOut={() => {
												setHoverPercentage(-100);
												setShowHoverPlayer(false);
											}}
										>
											{hasChapters && (
												<Chapters
													chapters={chapters}
													tabIndex={tabIndexValue}
													onMouseDown={handleMouseDownProgressBar}
													onClick={handleClickOnProgressBar}
													onKeyDown={handleKeyDownOnProgressBar}
													color={color}
													bottomControlBarRef={bottomControlBar}
													showHoverPlayer={showHoverPlayer}
													hoverTiming={hoverTiming}
													isDragging={isDragging}
													onFocus={() => {
														setProgressBarFocused(true);
													}}
													onBlur={() => {
														setProgressBarFocused(false);
													}}
												/>
											)}
											{!hasChapters && (
												<div
													className={joinClassNames(
														'bottomControlbarProgress',
														showHoverPlayer ? 'bottomControlbarProgress--hovered' : ''
													)}
													tabIndex={tabIndexValue}
													aria-label={translator('PROGRESS_BAR')}
													aria-valuemin={0}
													role="slider"
													aria-valuenow={Math.abs(currentPosition)}
													aria-valuetext={
														translator('POSITION_TEXT') +
														NumberHelper.formatHumanize(currentPosition, translator) +
														translator('LABEL_OF') +
														NumberHelper.formatHumanize(duration, translator)
													}
													onFocus={() => {
														setProgressBarFocused(true);
													}}
													onBlur={() => {
														setProgressBarFocused(false);
													}}
													onKeyDown={handleKeyDownOnProgressBar}
												>
													<div
														className="bottomControlbarProgressBarHover"
														style={{
															transform: `translateX(${hoverPercentage}%)`,
															backgroundColor: tinycolor(color).lighten(35).toString(),
														}}
													></div>
													<div
														className={joinClassNames(
															'bottomControlbarProgressBar',
															isMediaLive ? "bottomControlbarProgressBar--live-init" : '',
															mouseDownOnTimeline.current ? '' : 'bottomControlbarProgressBar--animation'
														)}
														style={
															isMediaLive && indicatorPosition === 0
																? { transform: `translateX(0%)`, ...primaryColor }
																: { ...progressTranslate, ...primaryColor }
														}
													></div>
												</div>
											)}

											<div
												className={joinClassNames([
													'bottomControlbarTooltipPlayerHover',
													showHoverPlayer ? 'bottomControlbarTooltipPlayerHoverHovered' : '',
													progressBarFocused ? 'bottomControlbarTooltipPlayerHoverFocused' : '',
												])}
												style={{
													transform: `translateX(${hoverPixels}px)`,
												}}
											>
												<div className="bottomControlbarTooltipPlayer">
													<div
														className={joinClassNames(
															'bottomControlbarPreviewWrapper',
															edgePosition ? `bottomControlbarPreviewWrapper--${edgePosition}` : ''
														)}
													>
														{!isMediaLive && videoHoverUrl && (
															<video
																ref={previewVideo}
																className="bottomControlbarPreviewVideo"
																src={videoHoverUrl}
															></video>
														)}
														{hoveredChapter && (
															<div className="bottomControlbarPreviewVideo-name">{hoveredChapter.name}</div>
														)}
														<span
															className={joinClassNames(
																'bottomControlbarHoverPosition',
																hasChapters && hoveredChapter ? 'bottomControlbarHoverPosition-chapter' : ''
															)}
														>
															{toHHMMSS(hoverTiming, isMediaLive)}
														</span>
													</div>
												</div>
											</div>

											<Draggable
												axis="x"
												bounds="parent"
												handle=".handle"
												position={{
													x: indicatorPosition - 1,
													y: 0,
												}}
											>
												<div
													className={joinClassNames(
														`handle bottomControlbarProgressIndicator`,
														showHoverPlayer || isDragging ? 'bottomControlbarProgressIndicator--visible' : '',
														mouseDownOnTimeline.current ? '' : 'bottomControlbarProgressBar--animation',
														hoveredChapter &&
															hoveredChapter.start <= currentPosition &&
															currentPosition <= hoveredChapter.end
															? 'bottomControlbarProgressIndicator--bigger'
															: ''
													)}
													style={isDragging ? { ...progressTranslate } : undefined}
												>
													<div className="bottomControlbarProgressIndicator--inner" style={{ ...primaryColor }} />
												</div>
											</Draggable>
										</div>
									</>
								)}
							<BottomControlSetting
								settings={settings}
								isSmallScreen
								isInlinePlayer={isInlinePlayer}
								isMaintainProportions={isMaintainProportions}
								queryParams={queryParams}
								isShowTimeline={isShowTimeline}
							/>
						</div>
						{settings.bottomControls?.showHoverShadow && <div className="bottomControlsShadow"></div>}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			className={joinClassNames(['bottomControlsContainer', isUsedOnMobile(queryParams) ? 'bottomControlsContainer--mobile' : ''])}
			onTouchStart={onTouchStart}
			onTouchMove={onTouchMove}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<div className="bottomIdControlsContainer">
				<div
					className={joinClassNames(
						'bottomControlbarWrapper',
						isDragging || playerHover || settings?.bottomControls?.alwaysVisible ? 'bottomControlbarHover' : ''
					)}
				>
					<div className="bottomControlbar" ref={(r) => (bottomControlBar.current = r)}>
						{settings.bottomControls.showSolidBackgroundBottomControl && <div style={solidColorStyle} className="solidBackground" />}
						{isShowTimeline &&
							(LiveHelper.getStreamType(isMediaLive, duration) === LiveHelper.LIVE_STREAM_TYPE_DVR ||
								LiveHelper.getStreamType(isMediaLive, duration) === LiveHelper.LIVE_STREAM_TYPE_ON_DEMAND) && (
								<>
									<div
										data-control-type="slider-control" // Every slider should have this attribute to be able to work.
										className={joinClassNames(
											'bottomControlbarProgressWrapper',
											hasChapters ? 'bottomControlbarProgressWrapper--chapters' : ''
										)}
										onMouseDown={handleMouseDownProgressBar}
										onMouseUp={(e) => e.stopPropagation}
										onClick={handleClickOnProgressBar}
										onMouseMove={handleMouseMoveProgressBar}
										onTouchStart={() => (mouseDownOnTimeline.current = true)}
										onMouseOver={() => {
											setShowHoverPlayer(true);
										}}
										onMouseOut={() => {
											setHoverPercentage(-100);
											setShowHoverPlayer(false);
										}}
									>
										{hasChapters && (
											<Chapters
												chapters={chapters}
												tabIndex={tabIndexValue}
												onMouseDown={handleMouseDownProgressBar}
												onClick={handleClickOnProgressBar}
												onKeyDown={handleKeyDownOnProgressBar}
												color={color}
												isMobile={isUsedOnMobile(queryParams)}
												bottomControlBarRef={bottomControlBar}
												showHoverPlayer={showHoverPlayer}
												hoverTiming={hoverTiming}
												isDragging={isDragging}
												onFocus={() => {
													setProgressBarFocused(true);
												}}
												onBlur={() => {
													setProgressBarFocused(false);
												}}
											/>
										)}
										{!hasChapters && (
											<div
												className={joinClassNames(
													'bottomControlbarProgress',
													showHoverPlayer ? 'bottomControlbarProgress--hovered' : ''
												)}
												tabIndex={tabIndexValue}
												aria-label={translator('PROGRESS_BAR')}
												aria-valuemin={0}
												role="slider"
												aria-valuenow={Math.abs(currentPosition)}
												aria-valuetext={
													translator('POSITION_TEXT') +
													NumberHelper.formatHumanize(currentPosition, translator) +
													translator('LABEL_OF') +
													NumberHelper.formatHumanize(duration, translator)
												}
												onFocus={() => {
													setProgressBarFocused(true);
												}}
												onBlur={() => {
													setProgressBarFocused(false);
												}}
												onKeyDown={handleKeyDownOnProgressBar}
											>
												<div
													className="bottomControlbarProgressBarHover"
													style={
														isUsedOnMobile(queryParams)
															? undefined
															: {
																	transform: `translateX(${hoverPercentage}%)`,
																	backgroundColor: tinycolor(color).lighten(35).toString(),
																}
													}
												/>
												<div
													className={joinClassNames(
														'bottomControlbarProgressBar',
														isMediaLive ? "bottomControlbarProgressBar--live-init" : '',
														mouseDownOnTimeline.current ? '' : 'bottomControlbarProgressBar--animation'
													)}
													style={
														isMediaLive && indicatorPosition === 0
															? { transform: `translateX(0%)`, ...primaryColor }
															: { ...progressTranslate, ...primaryColor }
													}
												></div>
											</div>
										)}

										<div
											className={joinClassNames([
												'bottomControlbarTooltipPlayerHover',
												showHoverPlayer ? 'bottomControlbarTooltipPlayerHoverHovered' : '',
												progressBarFocused ? 'bottomControlbarTooltipPlayerHoverFocused' : '',
											])}
											style={{
												transform: `translateX(${hoverPixels}px)`,
											}}
											onMouseOver={() => {
												setShowHoverPlayer(true);
											}}
											onMouseOut={() => {
												setHoverPercentage(-100);
												setShowHoverPlayer(false);
											}}
										>
											<div className="bottomControlbarTooltipPlayer">
												<div
													className={joinClassNames(
														'bottomControlbarPreviewWrapper',
														edgePosition ? `bottomControlbarPreviewWrapper--${edgePosition}` : ''
													)}
												>
													{!isMediaLive && videoHoverUrl && (
														<video
															ref={previewVideo}
															className="bottomControlbarPreviewVideo"
															src={videoHoverUrl}
														></video>
													)}
													{hoveredChapter && <div className="bottomControlbarPreviewVideo-name">{hoveredChapter.name}</div>}
													<span
														className={joinClassNames(
															'bottomControlbarHoverPosition',
															hasChapters && hoveredChapter ? 'bottomControlbarHoverPosition-chapter' : ''
														)}
													>
														{toHHMMSS(hoverTiming, isMediaLive)}
													</span>
												</div>
											</div>
										</div>

										<Draggable
											axis="x"
											bounds="parent"
											handle=".handle"
											onStart={isUsedOnMobile(queryParams) ? handleStartDrag : undefined}
											onStop={isUsedOnMobile(queryParams) ? handleStopDrag : undefined}
											onDrag={isUsedOnMobile(queryParams) ? handleOnDrag : undefined}
											position={
												isDragging && isUsedOnMobile(queryParams)
													? undefined
													: {
															x: indicatorPosition - (isMediaLive ? (isUsedOnMobile(queryParams) ? 12 : 14) : 3),
															y: 0,
														}
											}
										>
											<div
												className={joinClassNames(
													`handle bottomControlbarProgressIndicator`,
													isUsedOnMobile(queryParams) || isDragging ? 'bottomControlbarProgressIndicator--visible' : '',
													mouseDownOnTimeline.current ? '' : 'bottomControlbarProgressBar--animation',
													hoveredChapter && hoveredChapter.start <= currentPosition && currentPosition <= hoveredChapter.end
														? 'bottomControlbarProgressIndicator--bigger'
														: ''
												)}
												style={isDragging ? { ...progressTranslate } : undefined}
											>
												<div className="bottomControlbarProgressIndicator--inner" style={{ ...primaryColor }} />
											</div>
										</Draggable>
									</div>
								</>
							)}
						<BottomControlSetting
							settings={settings}
							playerHover={playerHover}
							isSmallScreen={isUsedOnMobile(queryParams)}
							isInlinePlayer={isInlinePlayer && isUsedOnMobile(queryParams)}
							isMaintainProportions={isMaintainProportions}
							alignVideoOnAndroidDevices={alignVideoOnAndroidDevices}
							queryParams={queryParams}
							isShowTimeline={isShowTimeline}
						/>
					</div>
					{settings.bottomControls?.showHoverShadow && <div className="bottomControlsShadow"></div>}
				</div>
			</div>
			{!isUsedOnMobile(queryParams) && !playerHover && <div className="bottomControlbarHearts" />}
		</div>
	);
};

export default BottomControls;
